/**
 * The terminal outcome: how the session ends up describing itself the moment the
 * clock stops (F-TR-05).
 *
 * This is the *engine's* verdict, and it is deliberately narrow — completed or
 * partial, decided by set membership in the ledger and nothing else. The archive
 * layer runs the record-level rules on top of it (`normalizeEffortOutcome`), which
 * can only ever downgrade this verdict: it turns a planless completion into
 * `recorded`, and it refuses `completed` to a segment that carries a skipped entry
 * even when the user later stepped back and ran it out. Keeping the two layers
 * separate is what lets the live summary state an outcome before a record exists,
 * without either layer having to know the other's inputs.
 *
 * Membership, never a duration comparison. Entry durations are reconciled to the
 * session total before a record is sealed, so a session whose per-step
 * measurements summed a few milliseconds above the session clock has every entry
 * scaled *down* — and a duration threshold would then archive a fully completed
 * workout as partial, permanently.
 */

import { LIVE_SPOKEN_CUES, isRequiredSegment } from "@ff/domain";
import type { EffortOutcome, SessionSegment, WorkoutExecutionLedger } from "@ff/domain";

/**
 * What a finished session can be. `recorded` is absent on purpose: it describes a
 * record with no plan to complete, and a plan is the only thing this engine runs.
 */
export type TerminalOutcome = Extract<EffortOutcome, "completed" | "partial">;

/**
 * Every segment the session had to perform to call itself complete.
 *
 * Preparation and recovery are excluded because skipping a countdown or a rest is
 * not a failure to train, and a zero-duration segment is excluded because there
 * was nothing in it to perform. The predicate lives in `@ff/domain` so the live
 * verdict and the archived one cannot drift apart.
 */
export function requiredSegments(
  segments: readonly SessionSegment[],
): readonly SessionSegment[] {
  return segments.filter(isRequiredSegment);
}

/**
 * `completed` only when every required segment is in the ledger's completed set;
 * otherwise `partial`.
 *
 * A plan with no required segments at all — a lone warm-up of zero length, a
 * preparation-only stub — completes, because there was nothing left undone. That
 * is the honest reading of "every", and refusing it would mark an empty plan as a
 * partial failure the user could never fix.
 */
export function terminalOutcome(
  segments: readonly SessionSegment[],
  ledger: WorkoutExecutionLedger,
): TerminalOutcome {
  const earned = new Set<string>(ledger.completedSegmentIds);
  const complete = requiredSegments(segments).every((segment) => earned.has(segment.id));
  return complete ? "completed" : "partial";
}

/**
 * The spoken line at the end of the session (F-TR-16).
 *
 * Two bells play either way; only the words differ, and they differ because
 * "Workout complete" is a claim about the plan. Announcing it over a session the
 * user cut short would be the audio equivalent of archiving a partial as
 * completed.
 */
export function terminalOutcomeAnnouncement(outcome: TerminalOutcome): string {
  return outcome === "completed"
    ? LIVE_SPOKEN_CUES.workoutComplete
    : LIVE_SPOKEN_CUES.workoutEnded;
}
